import React, { useState } from "react";
import { messages } from "./i18n-messages";
import { DialogBox, DialogBoxFooterType, Loader } from "~/core";
import { useAppSelector } from "~/store/hooks";
import {
    getDoesJdClientExist,
    getFieldInfoState,
    getIsAddOrIsUnarchive,
    getisAddOrUnarchiveBoundaryInPortalModalOpen,
} from "~/action-panel/components/field-module/selectors";
import { getToolsetPayload } from "~/map/components/map-tools/selectors";
import { getFieldEditGeometries } from "~/map/components/map-tools/selectors";
import { injectIntl, intlShape } from "react-intl";
import { useDispatch } from "react-redux";
import { actions as fieldActions } from "~/action-panel/components/field-module";
import { RecAPI } from "@ai360/core";
import { getUser } from "~/login/selectors";
import { MSGTYPE, actions as notificationActions } from "~/notifications";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import { getTheUserCompanyGuid } from "~/login";
import { logFirebaseEvent } from "~/utils/firebase";
import "./add-boundary-to-portal.css";

function AddBoundaryToPortal({ intl: { formatMessage } }) {
    const isOpen = useAppSelector(getisAddOrUnarchiveBoundaryInPortalModalOpen);
    const isAddOrIsUnarchive = useAppSelector(getIsAddOrIsUnarchive);
    const jdClientExists = useAppSelector(getDoesJdClientExist);
    const fieldInfo = useAppSelector(getFieldInfoState);

    // name and GUID on our end (for field adds)
    const farmName = fieldInfo.fieldModel?.farmName;
    const fieldName = fieldInfo.fieldModel?.fieldName;
    const fieldGuid = fieldInfo.fieldModel?.fieldGuid;

    // name and ID on the JD Ops Center end (for field unarchives)
    const fieldNameInJd = useAppSelector(getFieldInfoState).fieldName;
    const fieldIdInJd = useAppSelector(getFieldInfoState).fieldId;

    const userGuid = useAppSelector(getUser).userGuid;
    const companyGuid = useAppSelector(getTheUserCompanyGuid);
    const customerGuid = useAppSelector(getToolsetPayload).customerGuid;
    const geometries = useAppSelector(getFieldEditGeometries);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const dispatch = useDispatch();

    const addOrUnarchiveBoundary = async () => {
        logFirebaseEvent("portal_jd_ops_add_field_yes");
        setShowLoader(true);
        const geometriesForAdd = geometries.map((x) =>
            webMercatorUtils.webMercatorToGeographic(x).toJSON()
        );
        const request = {
            geometries: JSON.stringify(geometriesForAdd),
            farmName: farmName,
            fieldName: isAddOrIsUnarchive.includes("ADD") ? fieldName : fieldNameInJd,
            fieldId: fieldIdInJd,
            fieldGuid: fieldGuid,
            isIrrigated: false,
            isAdd: isAddOrIsUnarchive.includes("ADD"),
        };
        const response = await RecAPI.addBoundaryToJdOpsCenter(
            request,
            companyGuid,
            customerGuid,
            userGuid
        );
        if (!response.success) {
            dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.ERROR));
        } else {
            dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.SUCCESS));
        }

        setShowLoader(false);
        dispatch(fieldActions.setAddOrUnarchiveBoundaryInPortalModalOpen(false, false));
    };

    let messageContent;
    switch (isAddOrIsUnarchive) {
        case "ADD":
            messageContent = (
                <div className="add-or-update-dialog-message">
                    <div className="add-or-update-line">
                        {jdClientExists
                            ? formatMessage(messages.fieldHasNoConflictsOrMatches)
                            : formatMessage(messages.newClientFieldHasNoConflictsOrMatches)}
                    </div>
                    <div className="add-or-update-line">
                        {formatMessage(messages.wantToAddField)}
                    </div>
                </div>
            );
            break;
        case "UNARCHIVE":
            messageContent = (
                <div className="add-or-update-dialog-message">
                    <div className="add-or-update-line">
                        {formatMessage(messages.fieldHasNoConflictsOrMatchesButIsArchived)}
                    </div>
                    <div className="add-or-update-line">
                        {formatMessage(messages.wantToUnarchiveField)}
                    </div>
                </div>
            );
            break;
    }

    return (
        <DialogBox
            draggable={true}
            isOpen={isOpen}
            unrestricted={true}
            footerType={DialogBoxFooterType.YES_NO}
            title={
                isAddOrIsUnarchive?.includes("ADD")
                    ? formatMessage(messages.titleForAdd)
                    : formatMessage(messages.titleForUnarchive)
            }
            onAction={() => {
                addOrUnarchiveBoundary();
            }}
            onClose={() => {
                logFirebaseEvent("portal_jd_ops_add_field_no");
                dispatch(fieldActions.setAddOrUnarchiveBoundaryInPortalModalOpen(false, false));
            }}
        >
            {showLoader && <Loader />}
            {messageContent}
        </DialogBox>
    );
}

AddBoundaryToPortal.propTypes = {
    intl: intlShape,
};

export default injectIntl(AddBoundaryToPortal);
